import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { useTranslation, Trans } from 'react-i18next'
import { FaImages } from 'react-icons/fa'
import ConsentCheckbox from '../components/ConsentCheckbox'
import ScrollToTopButton from '../components/ScrollToTopButton'
import MobileQuoteButton from '../components/MobileQuoteButton'
import MondayManagement from '../pages/admin/MondayManagement'
import GalleryModal from '../components/GalleryModal'
import { useGalleries, useGallery } from '../lib/galleries'
import '../index.css'

const FORM_THANK_YOU_MAP = {
    roofing_new_project: '/forms/roofing/new-quote/thank-you',
}

const ROOFING_ICONS = [
    '/roofing/icon 1.webp',
    '/roofing/icon 2.webp',
    '/roofing/icon 3.webp',
]

const HERO_IMAGE = '/roofing/hero.webp'

const RoofingSection = () => {
    const { t } = useTranslation()
    const [showMondayForm, setShowMondayForm] = useState(false)
    const [activeFormType, setActiveFormType] = useState('roofing_new_project')
    const preModalPath = useRef(null)
    const [galleryOpen, setGalleryOpen] = useState(false)
    const [activeGalleryKey, setActiveGalleryKey] = useState(null)
    const { galleries } = useGalleries()
    const { images: activeGalleryImages, loading: galleryLoading } = useGallery(activeGalleryKey)
    const roofingGallery = galleries.find(g => g.service_key === 'roofing')

    const FORM_PATHS = { roofing_new_project: '/forms/roofing/new-quote' }

    const openModal = (formType) => {
        preModalPath.current = window.location.pathname
        window.history.pushState({ modal: true }, '', FORM_PATHS[formType] || '/forms/roofing/new-quote')
        setActiveFormType(formType)
        setShowMondayForm(true)
    }

    const closeModal = () => {
        setShowMondayForm(false)
        window.history.replaceState(null, '', preModalPath.current || '/roofing')
    }

    useEffect(() => {
        const handlePopState = () => setShowMondayForm(false)
        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [])

    const servicesList = t('roofing.services_list', { returnObjects: true })
    const roofingServices = Array.isArray(servicesList) ? servicesList.map((service, index) => ({
        ...service,
        icon: ROOFING_ICONS[index],
        color: 'bg-[#ffe662]'
    })) : []

    const inspectionBullets = t('roofing.inspection.bullets', { returnObjects: true })
    const installationBullets = t('roofing.installation.bullets', { returnObjects: true })
    const commercialBullets = t('roofing.commercial.bullets', { returnObjects: true })

    return (
        <section id="roofing" className="font-sans text-black bg-black">

            {/* 1. HERO SECTION */}
            <div className="flex overflow-hidden relative justify-center items-center min-h-screen">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.2 }}
                    className="absolute inset-0"
                >
                    <img src={HERO_IMAGE} alt={t('roofing.hero_alt')} className="object-cover w-full h-full" />
                    <div className="absolute inset-0 bg-black/60" />
                </motion.div>

                <div className="flex relative z-10 flex-col justify-between px-4 pt-32 pb-12 w-full h-full text-left sm:pb-24 sm:px-6 lg:px-8">
                    <motion.h1
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
                        className="text-white text-6xl sm:text-7xl md:text-8xl lg:text-[7rem] font-black leading-[0.9] tracking-tighter uppercase mb-8 sm:mb-12 lg:mb-16"
                    >
                        {t('roofing.hero_title')}
                    </motion.h1>

                    <div className="flex flex-wrap gap-6 justify-between items-end w-full">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
                            className="space-y-4 max-w-3xl text-xl leading-relaxed text-white sm:text-2xl"
                        >
                            <p>
                                <Trans
                                    i18nKey="roofing.hero_text_1"
                                    components={{ 1: <strong className="text-white" />, 3: <strong className="text-white" /> }}
                                />
                            </p>
                            <p>
                                <Trans
                                    i18nKey="roofing.hero_text_2"
                                    components={{ 1: <strong className="text-white" /> }}
                                />
                            </p>
                            <p>
                                {t('roofing.hero_text_3')}
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.6, ease: 'easeOut' }}
                            className="hidden flex-shrink-0 sm:flex flex-wrap gap-4"
                        >
                            <button
                                onClick={() => openModal('roofing_new_project')}
                                className="px-8 py-4 bg-[#ffe662] text-black font-bold text-sm tracking-widest uppercase rounded-lg hover:bg-[#ebd045] transition-colors min-w-[220px]"
                            >
                                {t('roofing.replacement.cta')}
                            </button>
                            {roofingGallery && (
                                <button
                                    onClick={() => { setActiveGalleryKey('roofing'); setGalleryOpen(true) }}
                                    className="inline-flex items-center gap-2 px-8 py-4 text-sm font-bold tracking-widest uppercase rounded-lg border-2 border-white text-white hover:bg-white hover:text-black transition-all duration-200 min-w-[220px]"
                                >
                                    <FaImages />
                                    {t('construction.view_gallery')}
                                </button>
                            )}
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* 2. SERVICES MENU */}
            <div className="flex overflow-hidden relative items-center min-h-screen bg-center bg-cover" style={{ backgroundImage: `url('/roofing/services-bg.webp')` }}>
                <div className="absolute inset-0 backdrop-blur-sm bg-black/40" />

                <div className="relative z-10 px-4 py-16 mx-auto w-full max-w-7xl text-center text-white sm:px-6 lg:px-8">
                    <motion.h2
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-6xl sm:text-7xl md:text-8xl lg:text-[7rem] font-black leading-[0.9] tracking-tighter uppercase mb-24"
                    >
                        {t('roofing.services_title')}
                    </motion.h2>

                    <motion.div
                        initial='hidden'
                        whileInView='visible'
                        viewport={{ once: true }}
                        variants={{
                            visible: { transition: { staggerChildren: 0.2 } },
                        }}
                        className="grid grid-cols-1 gap-12 justify-items-center md:grid-cols-3"
                    >
                        {roofingServices.map((service) => (
                            <motion.div
                                key={service.title}
                                variants={{
                                    hidden: { opacity: 0, y: 30 },
                                    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
                                }}
                                whileHover={{ scale: 1.05 }}
                                className="flex flex-col gap-6 items-center cursor-pointer group"
                                onClick={() => {
                                    const el = document.getElementById(service.anchor)
                                    if (el) el.scrollIntoView({ behavior: 'smooth' })
                                }}
                            >
                                <div className="w-64 h-64 rounded-full bg-[#ffe662]/80 flex items-center justify-center shadow-xl backdrop-blur-sm">
                                    <img src={service.icon} alt={service.title} className="object-contain w-32 h-32 opacity-80" />
                                </div>
                                <h3 className="text-2xl font-bold text-[#ffe662] drop-shadow-md">{service.title}</h3>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </div>

            {/* DETAILED SECTIONS */}
            {roofingServices.map((service, index) => {
                const details = t(`roofing.${service.anchor}`, { returnObjects: true })
                const isEven = index % 2 === 0
                const bullets = details.bullets || []

                // Cycle through available images
                const coverImage = index === 0 ? '/roofing/cover 2.webp' : // Replacement
                    index === 1 ? '/roofing/cover 3.webp' : // Repairs
                        '/roofing/cover 1.webp'  // Permits & Inspections

                return (
                    <div key={service.anchor} id={service.anchor} className={`py-24 ${isEven ? 'bg-[#f9f9f9]' : 'bg-white'} overflow-hidden`}>
                        <div className="grid grid-cols-1 gap-16 items-center px-6 mx-auto max-w-7xl lg:grid-cols-2">
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className={`order-2 ${isEven ? 'lg:order-2' : 'lg:order-1'} relative flex justify-center`}
                            >
                                <div className={`rounded-full overflow-hidden aspect-square border-8 ${isEven ? 'border-white' : 'border-[#f5f5f5]'} shadow-2xl max-w-lg w-full`}>
                                    <img src={coverImage} alt={details.img_alt} className="object-cover w-full h-full" />
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className={`order-1 ${isEven ? 'lg:order-1' : 'lg:order-2'}`}
                            >
                                <div className="flex gap-4 items-center mb-6">
                                    <img src={service.icon} alt={details.icon_alt} className="object-contain w-16 h-16" />
                                    <h2 className="text-4xl font-bold leading-tight md:text-5xl">{details.title}</h2>
                                </div>

                                <p className="mb-8 text-xl">
                                    {details.description}
                                </p>

                                <ul className="space-y-6 text-lg leading-snug">
                                    {Array.isArray(bullets) && bullets.map((item, idx) => (
                                        <motion.li
                                            key={idx}
                                            className="flex gap-3 items-start"
                                            initial={{ opacity: 0, x: -20 }}
                                            whileInView={{ opacity: 1, x: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.5, delay: idx * 0.1 }}
                                        >
                                            <span className="w-2 h-2 bg-black rounded-full mt-2.5 flex-shrink-0" />
                                            <span>{item}</span>
                                        </motion.li>
                                    ))}
                                </ul>

                                <motion.button
                                    onClick={() => openModal('roofing_new_project')}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: 0.6 }}
                                    className="mt-10 px-8 py-3 bg-[#ffe662] text-black font-bold text-xl rounded-full shadow-lg hover:bg-[#ebd045] transition-colors inline-block text-center"
                                >
                                    {details.cta}
                                </motion.button>
                            </motion.div>
                        </div>
                    </div>
                )
            })}

            {/* Form Section */}
            <div id="quote-form" className="px-4 py-24 bg-gray-50 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto bg-white rounded-[3rem] p-8 sm:p-16 shadow-2xl">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="mb-16 text-center"
                    >
                        <h2 className="mb-4 text-4xl font-black text-black sm:text-5xl">{t('roofing.form_title')}</h2>
                        <p className="text-xl text-gray-500">{t('roofing.form_subtitle')}</p>
                    </motion.div>

                    <div className="grid gap-12 lg:grid-cols-2">
                        <form
                            action="https://formsubmit.co/info@jdrivero.com"
                            method="POST"
                            className="contents"
                        >
                            <input type="hidden" name="_subject" value={t('form.subjects.roofing', 'New Roofing Quote Request')} />
                            <input type="hidden" name="_template" value="table" />
                            <input type="hidden" name="_next" value="https://www.jdrivero.com/success" />
                            <input type="hidden" name="_captcha" value="false" />

                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="space-y-8"
                            >
                                <div className="grid gap-6 sm:grid-cols-2">
                                    <label className="block space-y-3">
                                        <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.first_name')}</span>
                                        <input
                                            type="text"
                                            name={t('form.first_name_label', 'First Name')}
                                            className="w-full bg-gray-50 border-b-2 border-gray-200 focus:border-[#ffe662] outline-none py-3 text-lg transition-colors"
                                            required
                                        />
                                    </label>
                                    <label className="block space-y-3">
                                        <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.last_name')}</span>
                                        <input
                                            type="text"
                                            name={t('form.last_name_label', 'Last Name')}
                                            className="w-full bg-gray-50 border-b-2 border-gray-200 focus:border-[#ffe662] outline-none py-3 text-lg transition-colors"
                                            required
                                        />
                                    </label>
                                </div>
                                <label className="block space-y-3">
                                    <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.email')}</span>
                                    <input
                                        type="email"
                                        name={t('form.email_label', 'Email')}
                                        className="w-full bg-gray-50 border-b-2 border-gray-200 focus:border-[#ffe662] outline-none py-3 text-lg transition-colors"
                                        required
                                    />
                                </label>
                                <label className="block space-y-3">
                                    <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.phone')}</span>
                                    <input
                                        type="tel"
                                        name={t('form.phone_label', 'Phone Number')}
                                        pattern="^[+]?[0-9 \\-]{10,15}$"
                                        title={t('form.phone_error', 'Phone number must be between 10 and 15 digits.')}
                                        className="w-full bg-gray-50 border-b-2 border-gray-200 focus:border-[#ffe662] outline-none py-3 text-lg transition-colors"
                                        required
                                    />
                                </label>
                                <label className="block space-y-3">
                                    <span className="text-xs font-bold tracking-widest text-gray-400 uppercase">{t('form.project_details')}</span>
                                    <textarea
                                        name={t('form.project_details_label', 'Project Details')}
                                        rows={4}
                                        className="w-full bg-gray-50 border-b-2 border-gray-200 focus:border-[#ffe662] outline-none py-3 text-lg transition-colors resize-none"
                                        required
                                    />
                                </label>
                                <ConsentCheckbox />
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="bg-[#ffe662]/10 p-8 rounded-3xl space-y-8"
                            >
                                <fieldset className="space-y-6">
                                    <legend className="mb-6 text-sm font-bold tracking-widest text-black uppercase">{t('form.type_label')}</legend>
                                    {['Residential', 'Commercial', 'Civil'].map((typeKey) => (
                                        <label key={typeKey} className="flex gap-4 items-center cursor-pointer group">
                                            <div className="relative flex items-center justify-center w-6 h-6 bg-white border-2 border-gray-300 rounded-full group-hover:border-[#ffe662] transition-colors">
                                                <input type="radio" name="projectType" value={typeKey} required className="absolute inset-0 w-full h-full appearance-none cursor-pointer peer" />
                                                <div className="w-3 h-3 bg-[#ffe662] rounded-full scale-0 peer-checked:scale-100 transition-transform" />
                                            </div>
                                            <span className="text-lg font-medium text-gray-700 transition-colors group-hover:text-black">
                                                {t(`form.${typeKey.toLowerCase()}`)}
                                            </span>
                                        </label>
                                    ))}
                                </fieldset>
                                <fieldset className="space-y-6">
                                    <legend className="mb-6 text-sm font-bold tracking-widest text-black uppercase">{t('form.service_interest')}</legend>
                                    {roofingServices.map((service) => (
                                        <label key={service.title} className="flex gap-4 items-center cursor-pointer group">
                                            <div className="relative flex items-center justify-center w-6 h-6 bg-white border-2 border-gray-300 rounded-full group-hover:border-[#ffe662] transition-colors">
                                                <input type="radio" name="roofing-service" value={service.title} required className="absolute inset-0 w-full h-full appearance-none cursor-pointer peer" />
                                                <div className="w-3 h-3 bg-[#ffe662] rounded-full scale-0 peer-checked:scale-100 transition-transform" />
                                            </div>
                                            <span className="text-lg font-medium text-gray-700 transition-colors group-hover:text-black">{service.title}</span>
                                        </label>
                                    ))}
                                </fieldset>
                                <button type="submit" className="w-full rounded-2xl bg-[#ffe662] hover:bg-[#ebd045] px-8 py-5 text-black font-black uppercase tracking-[0.2em] shadow-lg transition-all hover:scale-[1.02]">
                                    {t('form.send')}
                                </button>
                            </motion.div>
                        </form>
                    </div>
                </div>
            </div>
            <GalleryModal images={activeGalleryImages.map(img => img.url)} isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} loading={galleryLoading} />
            {/* Scroll To Top Button */}
            <MobileQuoteButton onClick={() => openModal('roofing_new_project')} />
            <ScrollToTopButton />

            {/* Monday Form Modal */}
            {showMondayForm && (
                <div className="overflow-y-auto fixed inset-0 z-50">
                    <div className="flex justify-center items-center p-4 min-h-screen">
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                            onClick={closeModal}
                        />
                        {/* Modal Content */}
                        <div className="relative bg-white rounded-2xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                            <div className="overflow-y-auto max-h-[90vh]">
                                <MondayManagement
                                    session={null}
                                    isPublicMode={true}
                                    defaultFormType={activeFormType}
                                    onReturn={closeModal}
                                    thankYouPath={FORM_THANK_YOU_MAP[activeFormType]}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    )
}

export default RoofingSection
